import { objectiveCriterionGroups } from '../protocol/criterion-groups.js';
import type { ObjectiveStatus, ProtocolObjective, ProtocolStep } from '../schemas/protocol.js';
import type {
  ScribeCriterionJudgement,
  ScribeReport,
  SnapshotBand,
  SnapshotCriterion,
  SnapshotObjective,
} from '../schemas/turn.js';
import {
  type MatterQuantity,
  accumulateMatter,
  literalExtracts,
  matterQuantity,
  quantityReached,
} from './extracts.js';

/**
 * The STATUSES of §7.3 and what moves them: scores against thresholds, matter
 * against the expected quantity, and the two ways an objective can fail.
 *
 * « Le modèle juge bien et compte mal, le code compte bien et ne juge pas »
 * (§8.5). Everything here is the second half of that sentence: nothing in this
 * file decides whether an answer is good, and nothing outside it decides
 * whether an objective is reached.
 */

/** An objective is OPEN while its status is `vide`: the two others are settled. */
export function isOpen(status: ObjectiveStatus): boolean {
  return status === 'empty';
}

/**
 * The objectives half of a snapshot - all any of these rules reads of it.
 *
 * Taken `readonly` and as a narrow shape so a caller may hand over the state it
 * is building mid-turn, and not only a snapshot that already exists.
 */
export type ObjectiveStates = { readonly objectives: readonly SnapshotObjective[] };

/** The state of one objective in a snapshot, or the empty state it starts from. */
export function objectiveState(snapshot: ObjectiveStates, objectiveId: string): SnapshotObjective {
  const held = snapshot.objectives.find((entry) => entry.objectiveId === objectiveId);
  return held ?? { objectiveId, status: 'empty', extracts: [], criteria: [] };
}

/* ------------------------------------------------- the scales, as arithmetic */

/**
 * The scale of one criterion, as the PURE engine takes it: the bands' bounds
 * and the score « rempli » begins at.
 *
 * Bounds and not the bands themselves: the engine compares numbers and never
 * shows a description, so the texts of §6 have no business crossing into it.
 * `plancherBound` of `criterion/scoring.ts` derives the floor, the backend's
 * `criterionScalesOf` builds these off the FROZEN versions (§5.2), and nothing
 * here knows that a criterion has an editor.
 */
export type CriterionScale = {
  /** « À partir d'elle, le critère est rempli » (§6): the plancher band's lower bound. */
  readonly plancher: number;
  /** Where « passable » begins (§6, #110): the passable band's lower bound, or null. */
  readonly passable: number | null;
  /** Lower included, upper excluded, the last one including 100 (§6). */
  readonly bands: readonly SnapshotBand[];
};

/**
 * Per criterion id, the scale its FROZEN version poses, or `null` when that
 * version poses none - which is the criterion judged on a VERDICT (§6, §8.2).
 *
 * A criterion ABSENT from the record is a third thing and not a fourth: its
 * frozen version could not be read at all. It is judged like an unscored one -
 * on a verdict, which is what the prompt then asks for - and it is exempt from
 * the form check below, because « the wrong form » has no meaning when nobody
 * knows which form was right.
 */
export type CriterionScales = Readonly<Record<string, CriterionScale | null>>;

/** No scale known for anything - what a caller that judges nothing passes. */
export const NO_CRITERION_SCALES: CriterionScales = {};

/**
 * The band a score falls in, or null when the scale has none that covers it.
 *
 * « Une tranche va de sa borne basse incluse à sa borne haute exclue, la
 * dernière incluant 100 » (§6), which is why the last band is compared with
 * `<=` and every other with `<`: a score of exactly 100 belongs to the top
 * band, and a half-open test would leave it homeless.
 */
export function scoreBand(scale: CriterionScale, score: number): SnapshotBand | null {
  const last = scale.bands.length - 1;
  return (
    scale.bands.find((band, index) =>
      index === last
        ? score >= band.lower && score <= band.upper
        : score >= band.lower && score < band.upper,
    ) ?? null
  );
}

/**
 * What one criterion was judged on and what it was judged against - the four
 * fields `criterionSatisfied` compares, which are four of the snapshot's own.
 */
export type CriterionJudgement = Pick<
  SnapshotCriterion,
  'verdict' | 'score' | 'threshold' | 'plancher'
>;

/**
 * Is this criterion FILLED (§7.3, §8.2)?
 *
 * Two rules since the grill of 2026-09-16 (issue #79), and which one applies is
 * read off `plancher` - the mark of a frozen version that carries a notation:
 *
 * - **no plancher**, so no notation: the criterion is filled on the scribe's
 *   VERDICT and on nothing else. A threshold posed on it decided nothing (H47,
 *   the garde au gel), which the snapshot records by keeping both - a threshold
 *   beside a null plancher is exactly a threshold that was ignored;
 * - **a plancher**: the score reaches the threshold the objective posed, or,
 *   with none posed, the plancher of the criterion's own scale. One comparison,
 *   `>=`, because a threshold is a MINIMUM: a notation is monotone by
 *   construction, higher is better.
 *
 * **H18 is retired** and with it the « 50 par défaut » this function used to
 * fall back on: an unscored criterion has no score to compare, and a scored one
 * always has a plancher (§6 makes it required). There is no longer any state in
 * which the engine invents a bar nobody posed.
 *
 * A criterion the scribe has not judged carries a null verdict AND a null
 * score, and is satisfied by neither: a missing judgement is not a zero, not a
 * false, and not a pass.
 */
export function criterionSatisfied(judged: CriterionJudgement): boolean {
  if (judged.plancher === null) return judged.verdict === true;
  if (judged.score === null) return false;
  return judged.score >= (judged.threshold ?? judged.plancher);
}

/**
 * The criteria the scribe answered in the WRONG FORM - what marks a turn
 * malformed (§8.1, §8.2).
 *
 * « Zod exige la forme que la version gelée appelle : une note sur un critère
 * non noté, ou un verdict sur un critère noté, rend le tour malformé. » Zod
 * cannot ask that question on its own - `schemas/turn.ts` knows nothing of
 * criteria - so the two halves meet here, and the caller treats a non-empty
 * answer exactly as it treats an unreadable one: no status moves, the turn
 * counts, the raw text is kept.
 *
 * Refusing the whole turn rather than dropping the offending line, because a
 * scribe that judged a criterion by the wrong rule has judged the OBJECTIVE by
 * the wrong rule: keeping the rest would settle a status on a reading half of
 * which was thrown away, and nothing on the screen would say so.
 *
 * A criterion whose frozen version could not be read is skipped: see
 * `CriterionScales`.
 */
export function misjudgedCriteria(
  reading: ScribeReport,
  scales: CriterionScales,
): { readonly criterionId: string; readonly expected: 'score' | 'verdict' }[] {
  const wrong: { criterionId: string; expected: 'score' | 'verdict' }[] = [];
  for (const objective of reading.objectives) {
    for (const judgement of objective.criteria) {
      const scale = scales[judgement.criterionId];
      if (scale === undefined) continue;
      const expected = scale === null ? 'verdict' : 'score';
      const given = 'score' in judgement ? 'score' : 'verdict';
      if (given !== expected) wrong.push({ criterionId: judgement.criterionId, expected });
    }
  }
  return wrong;
}

/**
 * One group of an objective's criteria as the arithmetic reads it (issue #181):
 * a criterion alone, or an « l'un de » run of two or more. Filled as soon as
 * one of its criteria is; at its passable bar as soon as one of them is.
 */
export type ProgressGroup = {
  /** The shared `orGroup` label, or null for a criterion that stands alone. */
  readonly orGroup: string | null;
  readonly criteria: readonly SnapshotCriterion[];
  readonly satisfied: boolean;
  readonly passable: boolean;
};

/** Where one objective stands: its criteria, its matter, and whether it is reached. */
export type ObjectiveProgress = {
  readonly objectiveId: string;
  /** One entry per criterion the OBJECTIVE requires, in its own order. */
  readonly criteria: readonly SnapshotCriterion[];
  /**
   * The same criteria cut into the groups of §7.2 (issue #181): « l'un de » runs
   * and lone criteria. An objective with no `orGroup` has one group per
   * criterion, which is why nothing reads differently for a protocol written
   * before the groups.
   */
  readonly groups: readonly ProgressGroup[];
  readonly satisfiedGroups: number;
  readonly requiredGroups: number;
  /** Criteria, not groups: how many of the cited criteria are filled. */
  readonly satisfiedCriteria: number;
  readonly requiredCriteria: number;
  readonly quantity: MatterQuantity;
  readonly quantityReached: boolean;
  /** Every GROUP satisfied AND the quantity reached - the `réussi` of §7.3. */
  readonly reached: boolean;
  /**
   * Every GROUP satisfied OR at least at its passable bound, AND the
   * quantity reached (issue #112, #181): what the end of the step - and, since
   * #181, the turn the cap is reached on - validates « en orange » instead of
   * failing. True whenever `reached` is.
   */
  readonly passableReached: boolean;
};

/**
 * Is this criterion at least PASSABLE (#112)? Filled, or scored at or above the
 * passable bound its frozen scale marks. A criterion judged by verdict, or on a
 * scale that marks no passable band, has no middle: it is filled or it is not.
 */
function atLeastPassable(criterion: SnapshotCriterion): boolean {
  if (criterion.satisfied) return true;
  return (
    criterion.score !== null && criterion.passable !== null && criterion.score >= criterion.passable
  );
}

/**
 * The whole arithmetic of one objective, computed ONCE and read by three
 * callers: the status, the directive that says what is missing, and the
 * snapshot the reader of §12.2 replays. Three separate walks would be three
 * chances to compare a score against the wrong threshold.
 *
 * An objective with NO criterion is never reached, and that is not vacuous
 * truth turned upside down: §7.1 refuses to save a version carrying one, so
 * the case only exists on a draft - and « tous ses critères sont satisfaits »
 * read vacuously would succeed an objective before a word is said.
 */
export function objectiveProgress(
  objective: ProtocolObjective,
  state: SnapshotObjective,
): ObjectiveProgress {
  /*
    READ off the snapshot and never re-judged. « Un instantané gelé garde son
    satisfied d'origine » (§8.7): the turn that wrote it knew the frozen scale,
    and a reader replaying a session months later does not. `judgeCriteria` is
    the one place a verdict is turned into a `satisfied`, and it runs while the
    turn is being played.

    A requirement with no entry is one the scribe has never judged: it carries
    the objective's threshold so the reader can show what the bar WOULD have
    been, and satisfies nothing.
  */
  const held = new Map(state.criteria.map((entry) => [entry.criterionId, entry]));
  const criteria = objective.criteria.map(
    (requirement): SnapshotCriterion =>
      held.get(requirement.ref.id) ?? unjudgedCriterion(requirement.ref.id, requirement.threshold),
  );
  // Cut on the REQUIREMENTS (they carry the labels) and read on the snapshot
  // entries, which line up with them one for one.
  const groups = objectiveCriterionGroups(
    objective.criteria.map((requirement, index) => ({
      orGroup: requirement.orGroup,
      judged: criteria[index] ?? unjudgedCriterion(requirement.ref.id, requirement.threshold),
    })),
  ).map(
    (group): ProgressGroup => ({
      orGroup: group.orGroup,
      criteria: group.criteria.map((entry) => entry.judged),
      satisfied: group.criteria.some((entry) => entry.judged.satisfied),
      passable: group.criteria.some((entry) => atLeastPassable(entry.judged)),
    }),
  );

  const quantity = matterQuantity(state.extracts);
  const reachedQuantity = quantityReached(objective.quantity, quantity);
  const satisfied = criteria.filter((criterion) => criterion.satisfied).length;
  return {
    objectiveId: objective.id,
    criteria,
    groups,
    satisfiedGroups: groups.filter((group) => group.satisfied).length,
    requiredGroups: groups.length,
    satisfiedCriteria: satisfied,
    requiredCriteria: criteria.length,
    quantity,
    quantityReached: reachedQuantity,
    reached: groups.length > 0 && groups.every((group) => group.satisfied) && reachedQuantity,
    passableReached:
      groups.length > 0 && groups.every((group) => group.passable) && reachedQuantity,
  };
}

/**
 * The status of an objective after a turn (§7.3).
 *
 * - `vide` while nothing settles it - an objective holding matter that is not
 *   enough yet is still `vide`, because §7.3 names three statuses and « en
 *   cours » is not one of them;
 * - `réussi` at the first turn all its criteria are satisfied and its quantity
 *   is reached;
 * - `raté` in two cases only: an explicit refusal constated by the scribe, or
 *   the end of the step without success.
 *
 * **Both settled statuses are terminal until an invalidation.** « Un objectif
 * réussi ne redevient vide que par l'action invalider un objectif » (§7.3), and
 * the same is applied to `raté`: the scribe is only asked about the objectives
 * still open (§8.1), so a settled one receives no new matter to change its
 * mind with, and letting one flip on a stale reading is how a status starts
 * oscillating.
 *
 * A refusal and a reach landing on the SAME turn give `réussi`: the matter is
 * there, whatever the person said about not wanting to give it.
 */
export function objectiveStatusAfter(input: {
  readonly previous: ObjectiveStatus;
  readonly reached: boolean;
  readonly refused: boolean;
  readonly stepEnding: boolean;
  /** The passable bar of #112, read at the end of the step only. */
  readonly passableReached?: boolean;
}): ObjectiveStatus {
  if (input.previous !== 'empty') return input.previous;
  if (input.reached) return 'succeeded';
  if (input.refused) return 'failed';
  if (input.stepEnding) return input.passableReached === true ? 'succeeded' : 'failed';
  return 'empty';
}

/** One objective settling on this turn - what a condition of §8.4 listens for. */
export type ObjectiveStatusChange = {
  readonly objectiveId: string;
  readonly from: ObjectiveStatus;
  readonly to: 'succeeded' | 'failed';
};

/** What the reading of one turn does to the state, before any decision. */
export type ReadingOutcome = {
  /** The objectives of the snapshot, updated; the step's own ones added if new. */
  readonly objectives: readonly SnapshotObjective[];
  readonly changes: readonly ObjectiveStatusChange[];
  /** H4 hybride: no literal extract landed on an open objective AND the person did not answer. */
  readonly empty: boolean;
};

/**
 * The empty answer of §8.2, in its HYBRID form (H4 renversée le 2026-09-16,
 * #83): « un tour où le scribe n'attribue aucun extrait à aucun objectif ouvert
 * ET juge que la personne n'a pas répondu à la question ».
 *
 * Two halves, one per judge. The matter half is counted on the extracts that
 * SURVIVE the literality check, which is the only reading that holds: a turn
 * where the scribe invented three quotations gave the objectives nothing, and
 * calling it full would let a hallucinating model hold a step open forever.
 * The answer half is the scribe's `answered`, because « the person answered
 * without giving matter » is a judgement and the code cannot make it - which
 * is exactly why the count alone was wrong: it called every consent of an
 * opening step empty, and closed the step on it.
 *
 * So « je ne sais pas » and « ça me convient » are NOT empty here, as they were
 * never « ne répond pas du tout » for the guardrail of §8.5. A literal extract
 * that lands makes the turn full whatever the scribe said of the answer.
 */
export function emptyAnswer(
  reply: string,
  step: ProtocolStep,
  snapshot: ObjectiveStates,
  reading: ScribeReport,
): boolean {
  // Read off the reading rather than counted a second time: `applyReading`
  // already walks the open objectives and checks the same literality, and two
  // implementations of « la réponse est vide » would drift the day one of them
  // learns something the other does not.
  //
  // With NO scales, and it costs nothing: H4 counts EXTRACTS, and no scale can
  // make an extract appear or vanish. Only the `satisfied` of the criteria the
  // walk produces along the way would differ, and this caller reads none of it.
  return applyReading(step, snapshot, reply, reading, NO_CRITERION_SCALES).empty;
}

/**
 * §8.1 point 4, up to the statuses: check the literality of the extracts,
 * accumulate them per objective, count, read each criterion's verdict or
 * compare its score to its threshold or plancher, update the statuses.
 *
 * Only the OPEN objectives of the CURRENT step are read - « le scribe juge tous
 * les objectifs encore ouverts de l'étape courante » - so a report naming a
 * settled objective, or one of another step, is ignored rather than obeyed. The
 * objectives of the other steps stay in the state untouched: an invalidation may
 * aim at one (H20) and a direction may quote one (§8.6).
 *
 * `scales` is what the FROZEN criterion versions say (§5.2) and the only thing
 * this walk cannot derive from its other arguments: which criteria take a
 * verdict, which take a score, and where « rempli » begins for each. The
 * backend builds it once per turn from the versions the session started on.
 *
 * The end of the step is NOT applied here. Whether the step ends is a decision,
 * and the decision is taken on the statuses this function produces - computing
 * the failure first would make every condition listening on « un obligatoire
 * raté » fire on the turn that closes the step.
 */
export function applyReading(
  step: ProtocolStep,
  snapshot: ObjectiveStates,
  reply: string,
  reading: ScribeReport,
  scales: CriterionScales,
): ReadingOutcome {
  const updated = new Map<string, SnapshotObjective>(
    snapshot.objectives.map((entry) => [entry.objectiveId, entry]),
  );
  const changes: ObjectiveStatusChange[] = [];
  let attributed = 0;

  for (const objective of step.objectives) {
    const state = objectiveState(snapshot, objective.id);
    if (!isOpen(state.status)) {
      updated.set(objective.id, state);
      continue;
    }

    const said = reading.objectives.find((entry) => entry.objectiveId === objective.id);
    const kept = said === undefined ? [] : literalExtracts(reply, said.extracts);
    attributed += kept.length;

    const extracts = accumulateMatter(state.extracts, kept);
    const judged = judgeCriteria(objective, state, said?.criteria ?? [], scales);
    const progress = objectiveProgress(objective, { ...state, extracts, criteria: judged });
    const refused = reading.refusal.detected && reading.refusal.objectiveId === objective.id;
    const status = objectiveStatusAfter({
      previous: state.status,
      reached: progress.reached,
      refused,
      stepEnding: false,
    });

    if (status !== state.status && status !== 'empty') {
      changes.push({ objectiveId: objective.id, from: state.status, to: status });
    }
    updated.set(objective.id, {
      objectiveId: objective.id,
      status,
      extracts,
      criteria: [...progress.criteria],
      // A reading never validates « en orange »: only the end of the step does,
      // so a mark already there (on a settled, terminal status) is kept as is.
      ...(state.passableOnly === true ? { passableOnly: true } : {}),
    });
  }

  return {
    objectives: [...updated.values()],
    changes,
    empty: attributed === 0 && !reading.answered,
  };
}

/**
 * « Réussi en orange, au tour du plafond » (§7.3, issues #112 and #181): on the
 * turn a ceiling is reached, every objective of the step still open that has
 * reached its PASSABLE bar - every group at least passable, the quantity there -
 * becomes `réussi`, marked `passableOnly`.
 *
 * It runs on the reading and BEFORE the conditions are evaluated, and the
 * passage is a status change like any other: it is appended to `changes`, which
 * is what the triggers « un objectif passe à réussi » listen to. That is the
 * point of #181 - a condition « Succès ancré passe à réussi », ranged before a
 * condition « essais épuisés », wins on the capped turn instead of losing to it,
 * and the example is kept. It never runs before the cap: a turn does not
 * validate at passable, and an objective that reaches its threshold early is a
 * full `réussi` already (`applyReading`).
 *
 * An objective the reading just settled (`réussi`, or `raté` on a refusal) is
 * not open and is left alone, so the end-of-step rule of `nextSnapshot` finds
 * nothing of these to settle twice. `ReadingOutcome.empty` is carried over.
 */
export function settlePassableAtCap(step: ProtocolStep, read: ReadingOutcome): ReadingOutcome {
  const state = { objectives: read.objectives };
  const changes: ObjectiveStatusChange[] = [...read.changes];
  const settled = new Map<string, SnapshotObjective>();

  for (const objective of step.objectives) {
    const held = objectiveState(state, objective.id);
    if (!isOpen(held.status) || !objectiveProgress(objective, held).passableReached) continue;
    settled.set(objective.id, { ...held, status: 'succeeded', passableOnly: true });
    changes.push({ objectiveId: objective.id, from: held.status, to: 'succeeded' });
  }
  if (settled.size === 0) return read;

  return {
    ...read,
    objectives: read.objectives.map((entry) => settled.get(entry.objectiveId) ?? entry),
    changes,
  };
}

/**
 * A criterion nobody has judged yet - what an objective's requirement starts as.
 *
 * The plancher stays null because `objectiveProgress` READS the snapshot and
 * never knows the frozen scale (§8.7). That leaves the same pair as the garde
 * au gel (H47) - a threshold beside a null plancher - so a reader telling the
 * two apart has to look at the verdict: an ignored threshold went with one, an
 * unjudged requirement went with none.
 */
function unjudgedCriterion(criterionId: string, threshold: number | null): SnapshotCriterion {
  return {
    criterionId,
    verdict: null,
    score: null,
    threshold,
    plancher: null,
    band: null,
    satisfied: false,
    passable: null,
  };
}

/**
 * THE JUDGEMENT of one objective's criteria after a reading (§8.2) - the one
 * place a verdict or a score becomes a `satisfied`, and the one place the bar
 * each was measured against is frozen.
 *
 * One entry per REQUIREMENT of the objective and in its order, because that is
 * what the objective is judged on: a criterion the scribe named and the
 * objective does not require has judged nothing, and one the objective requires
 * and the scribe skipped still has to appear, unsatisfied.
 *
 * A criterion the report does not mention KEEPS its last judgement rather than
 * losing it: H19 notes every score on the cumulated matter, so the last one
 * given is still the judgement of that matter, and blanking it would unsatisfy
 * a criterion because the model was terse. What is recomputed every turn is
 * what the FROZEN version and the objective say - plancher, band, threshold -
 * so a held judgement is never carried beside a bar that is not its own.
 *
 * H47 needs no field: a threshold beside a null plancher IS a threshold this
 * walk could not apply, and the reader of §12.3 says so from the two.
 *
 * The two sides are INDEXED before the walk rather than searched per
 * requirement: an objective may require ten criteria and the report name ten,
 * and two `find` inside a `map` is the quadratic the `Map` of the old
 * `scoresAfter` did not have.
 */
function judgeCriteria(
  objective: ProtocolObjective,
  state: SnapshotObjective,
  said: readonly ScribeCriterionJudgement[],
  scales: CriterionScales,
): SnapshotCriterion[] {
  const heldById = new Map(state.criteria.map((entry) => [entry.criterionId, entry]));
  const saidById = new Map(said.map((entry) => [entry.criterionId, entry]));

  return objective.criteria.map((requirement): SnapshotCriterion => {
    const criterionId = requirement.ref.id;
    const judgement = saidById.get(criterionId);
    const held = heldById.get(criterionId);

    // Three cases and no nesting: nothing said this turn keeps what was held,
    // and a judgement replaces BOTH fields so a score never survives beside a
    // verdict that just contradicted it.
    const rendered =
      judgement === undefined
        ? { verdict: held?.verdict ?? null, score: held?.score ?? null }
        : 'score' in judgement
          ? { verdict: null, score: judgement.score }
          : { verdict: judgement.verdict, score: null };

    const scale = scales[criterionId] ?? null;
    const judged = {
      ...rendered,
      threshold: requirement.threshold,
      plancher: scale === null ? null : scale.plancher,
    };

    return {
      criterionId,
      ...judged,
      band: scale === null || judged.score === null ? null : scoreBand(scale, judged.score),
      satisfied: criterionSatisfied(judged),
      // Frozen beside the plancher for the reason the plancher is: the reader
      // replays the scale the turn was played on, never today's (§8.7).
      passable: scale === null ? null : scale.passable,
    };
  });
}
